import { z } from 'zod';

export const PLACE_NOTIFICATION_SEND_TYPES = [
  'CHAT',
  'CAMPAIGN',
  'APPOINTMENT',
  'SERVICE_ORDER',
  'QUOTE',
  'RECEPTION',
  'BIRTHDAY',
  'BIRTHDAY_COLLECTION',
  'MILEAGE',
  'NPS',
  'PICKUP_READY',
  'REVISIONS',
  'MAINTENANCE_ALERT',
] as const;

export const PlaceNotificationSendTypeSchema = z.enum(PLACE_NOTIFICATION_SEND_TYPES);
export type PlaceNotificationSendType = z.infer<typeof PlaceNotificationSendTypeSchema>;

export const PlaceTypeChannelFlagsSchema = z.object({
  inbox: z.boolean(),
  push: z.boolean(),
  chat: z.boolean(),
  whatsapp: z.boolean(),
});

export const PlaceTypeSettingSchema = z.object({
  enabled: z.boolean(),
  channels: PlaceTypeChannelFlagsSchema,
  titleTemplate: z.string().nullable(),
  bodyTemplate: z.string().nullable(),
});

export const PlaceTypeSettingPatchSchema = z.object({
  enabled: z.boolean().optional(),
  channels: PlaceTypeChannelFlagsSchema.partial().optional(),
  titleTemplate: z.string().nullable().optional(),
  bodyTemplate: z.string().nullable().optional(),
});

export const PlaceNotificationSettingsSchema = z.object({
  CHAT: PlaceTypeSettingSchema,
  CAMPAIGN: PlaceTypeSettingSchema,
  APPOINTMENT: PlaceTypeSettingSchema,
  SERVICE_ORDER: PlaceTypeSettingSchema,
  QUOTE: PlaceTypeSettingSchema,
  RECEPTION: PlaceTypeSettingSchema,
  BIRTHDAY: PlaceTypeSettingSchema,
  BIRTHDAY_COLLECTION: PlaceTypeSettingSchema,
  MILEAGE: PlaceTypeSettingSchema,
  NPS: PlaceTypeSettingSchema,
  PICKUP_READY: PlaceTypeSettingSchema,
  REVISIONS: PlaceTypeSettingSchema,
  MAINTENANCE_ALERT: PlaceTypeSettingSchema,
});

export const AlertCadenceUnitSchema = z.enum(['days', 'months']);
export const AlertCadenceIntervalSchema = z.object({
  count: z.number().int().min(1).max(365),
  unit: AlertCadenceUnitSchema,
});
export type AlertCadenceInterval = z.infer<typeof AlertCadenceIntervalSchema>;

export const PlaceNotificationConfigBodySchema = z.object({
  settings: z
    .object({
      CHAT: PlaceTypeSettingPatchSchema.optional(),
      CAMPAIGN: PlaceTypeSettingPatchSchema.optional(),
      APPOINTMENT: PlaceTypeSettingPatchSchema.optional(),
      SERVICE_ORDER: PlaceTypeSettingPatchSchema.optional(),
      QUOTE: PlaceTypeSettingPatchSchema.optional(),
      RECEPTION: PlaceTypeSettingPatchSchema.optional(),
      BIRTHDAY: PlaceTypeSettingPatchSchema.optional(),
      BIRTHDAY_COLLECTION: PlaceTypeSettingPatchSchema.optional(),
      MILEAGE: PlaceTypeSettingPatchSchema.optional(),
      NPS: PlaceTypeSettingPatchSchema.optional(),
      PICKUP_READY: PlaceTypeSettingPatchSchema.optional(),
      REVISIONS: PlaceTypeSettingPatchSchema.optional(),
      MAINTENANCE_ALERT: PlaceTypeSettingPatchSchema.optional(),
    })
    .optional(),
  npsDelayDays: z.number().int().min(0).max(365).optional(),
  mileageCadence: AlertCadenceIntervalSchema.optional(),
  maintenanceCadence: AlertCadenceIntervalSchema.optional(),
});
export type PlaceNotificationConfigBody = z.infer<typeof PlaceNotificationConfigBodySchema>;

export const PlaceNotificationConfigResponseSchema = z.object({
  placeId: z.uuid(),
  settings: PlaceNotificationSettingsSchema,
  npsDelayDays: z.number().int().nonnegative(),
  mileageCadence: AlertCadenceIntervalSchema,
  maintenanceCadence: AlertCadenceIntervalSchema,
  updatedAt: z.iso.datetime().nullable(),
});
export type PlaceNotificationConfigResponse = z.infer<typeof PlaceNotificationConfigResponseSchema>;

export const PartyAlertCadenceBodySchema = z.object({
  mileage: AlertCadenceIntervalSchema.nullable(),
  maintenance: AlertCadenceIntervalSchema.nullable(),
});
export type PartyAlertCadenceBody = z.infer<typeof PartyAlertCadenceBodySchema>;

export const PartyAlertCadenceResponseSchema = z.object({
  placeId: z.uuid(),
  partyId: z.uuid(),
  mileage: AlertCadenceIntervalSchema.nullable(),
  maintenance: AlertCadenceIntervalSchema.nullable(),
  resolvedMileage: AlertCadenceIntervalSchema,
  resolvedMaintenance: AlertCadenceIntervalSchema,
});
export type PartyAlertCadenceResponse = z.infer<typeof PartyAlertCadenceResponseSchema>;

export const ConsumerNotificationPreferenceBodySchema = z.object({
  type: z.enum([
    'CHAT_MESSAGE',
    'RECEPTION_CHECKLIST_PENDING',
    'RECEPTION_CHECKLIST_APPROVED',
    'RECEPTION_CHECKLIST_REJECTED',
    'SERVICE_ORDER_CREATED',
    'SERVICE_ORDER_STATUS_CHANGED',
    'SERVICE_QUOTE_SENT',
    'SERVICE_ORDER_ITEM_DECISION',
    'SERVICE_QUOTE_ITEM_DECISION',
    'SERVICE_ORDER_ITEM_ADDED',
    'SERVICE_ORDER_ITEM_EXECUTION_CHANGED',
    'CHECKOUT_CHECKLIST_PENDING',
    'CHECKOUT_CHECKLIST_APPROVED',
    'CHECKOUT_CHECKLIST_REJECTED',
    'APPOINTMENT_REMINDER_D1',
    'APPOINTMENT_REMINDER_T1H',
    'APPOINTMENT_CONFIRMED',
    'APPOINTMENT_CANCELLED',
    'APPOINTMENT_RESCHEDULED',
    'PLACE_CAMPAIGN',
    'BIRTHDAY',
    'BIRTHDAY_COLLECTION',
    'MILEAGE_UPDATE',
    'NPS_SURVEY',
    'PICKUP_READY',
    'REVISION_REMINDER',
    'MAINTENANCE_ALERT',
  ]),
  channel: z.enum(['inbox', 'push', 'chat', 'whatsapp']),
  optedOut: z.boolean(),
});
export type ConsumerNotificationPreferenceBody = z.infer<
  typeof ConsumerNotificationPreferenceBodySchema
>;

export const ConsumerNotificationPreferenceResponseSchema = z.object({
  ok: z.literal(true),
});
export type ConsumerNotificationPreferenceResponse = z.infer<
  typeof ConsumerNotificationPreferenceResponseSchema
>;

export const WhatsAppInboundBodySchema = z.object({
  from: z.string().min(1),
  body: z.string(),
  providerMessageId: z.string().min(1),
});
export type WhatsAppInboundBody = z.infer<typeof WhatsAppInboundBodySchema>;

export const WhatsAppInboundResponseSchema = z.object({
  accepted: z.boolean(),
  applied: z.boolean(),
  duplicate: z.boolean(),
});
export type WhatsAppInboundResponse = z.infer<typeof WhatsAppInboundResponseSchema>;
